import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { ExpandIcon, SparklesIcon, XIcon } from '@hero/web-icons';
import { heroEmblemWhite } from '@hero/web-assets';
import { Button } from '../../../ui/Button/Button';
import { useAgentChatStore } from './store';
import { useAgentChatManager } from './useAgentChatManager';
import { useAgentResumeManager } from './useAgentResumeManager';
import { MessageList } from './MessageList';
import { Composer } from './Composer';

/**
 * The core chat surface — bottom-right pinned panel, expandable to
 * a centred large window. The same component is reused by the
 * `/ai` full-page route by passing `layout='page'`.
 *
 * Keyboard shortcuts:
 * - `Cmd/Ctrl + K` toggles the panel from anywhere.
 * - `Esc` closes the panel when open.
 *
 * Visuals: dark glass top bar with gradient AI accent, soft drop
 * shadow, and a subtle "live" pulse on the avatar while streaming.
 * Bubbles are layered in `MessageList` so the conversation feels
 * cinematic, not boxy.
 */
export interface ChatPanelProps {
  /**
   * `floating` (default) — bottom-right pinned card.
   * `page` — fills its parent (used by the dedicated `/ai` route).
   */
  layout?: 'floating' | 'page';
}

export const ChatPanel = ({ layout = 'floating' }: ChatPanelProps) => {
  const { t } = useTranslation('m-agent');
  const isOpen = useAgentChatStore(s => s.open);
  const expanded = useAgentChatStore(s => s.expanded);
  const closePanel = useAgentChatStore(s => s.closePanel);
  const setExpanded = useAgentChatStore(s => s.setExpanded);
  const status = useAgentChatStore(s => s.status);
  const error = useAgentChatStore(s => s.error);
  const manager = useAgentChatManager();
  const containerRef = useRef<HTMLDivElement>(null);
  // The server-side thread outlives the tab. Cards it still counts as
  // open are offered as a resume strip above the empty state, so a
  // reload can't leave the agent asking about drafts the user can't see.
  useAgentResumeManager({ enabled: isOpen || layout === 'page' });

  useEffect(() => {
    if (layout === 'page') return;
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        useAgentChatStore.getState().togglePanel();
      }
      if (e.key === 'Escape' && useAgentChatStore.getState().open) {
        closePanel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closePanel, layout]);

  const isPage = layout === 'page';

  return (
    <AnimatePresence>
      {(isOpen || isPage) && (
        <motion.div
          ref={containerRef}
          initial={isPage ? false : { opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={isPage ? undefined : { opacity: 0, y: 16, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 240, damping: 24 }}
          className={cn(
            'flex flex-col overflow-hidden bg-white text-slate-900',
            isPage
              ? 'h-full w-full'
              : cn(
                  'fixed end-6 bottom-24 z-40 rounded-2xl shadow-2xl ring-1 ring-slate-200',
                  expanded
                    ? 'h-[calc(100vh-7rem)] w-[min(900px,95vw)]'
                    : 'h-[640px] max-h-[calc(100vh-7rem)] w-[min(420px,95vw)]',
                ),
          )}
        >
          <Header
            expanded={expanded}
            onToggleExpand={() => setExpanded(!expanded)}
            onClose={closePanel}
            isPage={isPage}
            statusLabel={statusLabelFor(status, t as unknown as TranslateFn)}
          />

          <div className='flex min-h-0 flex-1 flex-col'>
            <MessageList layout={layout} />

            <div
              className={cn(
                'shrink-0',
                isPage && 'mx-auto w-full max-w-3xl px-4 sm:px-6',
              )}
            >
              {error && (
                <div className='border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700'>
                  <div className='font-medium'>
                    {t(`errors.${error.code}`, error.fallback)}
                  </div>
                  {error.referenceId && (
                    <div className='mt-0.5 font-mono text-[10px] text-red-500/80'>
                      {t('errors.reference_id', 'Ref')}:{' '}
                      {error.referenceId.slice(0, 8)}
                    </div>
                  )}
                </div>
              )}

              <Composer
                disabled={status === 'streaming' || status === 'awaiting_confirmation'}
                onSubmit={text => manager.sendMessage(text)}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

type TranslateFn = (k: string, defaultValue?: string) => string;

interface HeaderProps {
  expanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  isPage: boolean;
  statusLabel?: string;
}

const Header = ({ expanded, onToggleExpand, onClose, isPage, statusLabel }: HeaderProps) => {
  const { t } = useTranslation('m-agent');
  return (
    <div
      className={cn(
        'relative overflow-hidden border-b border-slate-200',
        isPage && 'bg-gradient-to-br from-[#3532A7] via-[#3453D9] to-[#2563EB]',
      )}
    >
      {!isPage && (
        <div className='absolute inset-0 bg-gradient-to-br from-[#3532A7]/10 via-[#3453D9]/10 to-[#2563EB]/10' />
      )}
      {/* Decorative sheen — only on the full-page brand bar. */}
      {isPage && (
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]' />
      )}
      <div
        className={cn(
          'relative flex items-center gap-3',
          isPage ? 'mx-auto w-full max-w-3xl px-6 py-5' : 'px-4 py-3',
        )}
      >
        <div
          className={cn(
            'flex flex-none items-center justify-center overflow-hidden',
            isPage
              ? 'size-12 rounded-2xl bg-white/15 ring-1 ring-white/30 backdrop-blur'
              : 'size-9 rounded-full bg-gradient-to-br from-[#3532A7] via-[#3453D9] to-[#2563EB] shadow-md shadow-[#3532A7]/30',
          )}
        >
          <img
            src={heroEmblemWhite}
            alt={t('header.title', 'Hero AI')}
            className={cn('object-contain', isPage ? 'h-6 w-auto' : 'h-4 w-auto')}
          />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span
              className={cn(
                'font-semibold',
                isPage ? 'text-lg text-white' : 'text-sm text-slate-900',
              )}
            >
              {t('header.title', 'Hero AI')}
            </span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium',
                isPage
                  ? 'bg-white/15 text-white/90 ring-1 ring-white/25'
                  : 'bg-primary-100 text-primary-700',
              )}
            >
              {t('header.beta', 'Beta')}
            </span>
          </div>
          <div
            className={cn(
              'flex items-center gap-1.5 text-xs',
              isPage ? 'text-white/70' : 'text-slate-500',
            )}
          >
            <SparklesIcon
              className={cn('size-3', isPage ? 'text-white/80' : 'text-primary-500')}
            />
            <span>{statusLabel ?? t('header.tagline', 'Your logistics co-pilot')}</span>
          </div>
        </div>
        {!isPage && (
          <Button
            variant='ghost'
            size='icon'
            onClick={onToggleExpand}
            aria-label={
              expanded
                ? t('header.collapse', 'Collapse')
                : t('header.expand', 'Expand')
            }
            className='size-8 rounded-full text-slate-500 hover:bg-slate-100'
          >
            <ExpandIcon className='size-4' />
          </Button>
        )}
        {!isPage && (
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label={t('header.close', 'Close')}
            className='size-8 rounded-full text-slate-500 hover:bg-slate-100'
          >
            <XIcon className='size-4' />
          </Button>
        )}
      </div>
    </div>
  );
};

const statusLabelFor = (
  status: ReturnType<typeof useAgentChatStore.getState>['status'],
  t: TranslateFn,
): string | undefined => {
  switch (status) {
    case 'streaming':
      return t('header.thinking', 'Thinking…');
    case 'awaiting_confirmation':
      return t('header.awaiting_confirmation', 'Waiting for your confirmation');
    case 'error':
      return t('header.error', 'Something went wrong');
    case 'idle':
    default:
      return undefined;
  }
};
